import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getToken } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import {
  ChevronRight,
  ChevronDown,
  Building2,
  Megaphone,
  Layers,
  FileImage,
  Radio,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
  Search,
  SlidersHorizontal,
  TrendingUp,
  Users,
  DollarSign,
  CalendarCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Status badge colors ──────────────────────────────────────

const STATUS_STYLES = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  paused: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  archived: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
  pending: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  disapproved: 'bg-red-500/10 text-red-400 border-red-500/20',
  inactive: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
  disconnected: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const SYNC_ICONS = {
  synced: CheckCircle2,
  sync_failed: XCircle,
  pending_create: Clock,
  pending_update: Clock,
  pending_delete: Clock,
};

const SYNC_COLORS = {
  synced: 'text-emerald-500',
  sync_failed: 'text-red-500',
  pending_create: 'text-amber-400',
  pending_update: 'text-amber-400',
  pending_delete: 'text-amber-400',
};

const PLATFORM_LABELS = {
  meta: 'Meta',
  google: 'Google Ads',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  missionassist: 'MissionAssist',
  other: 'Other',
};

const PLATFORM_COLORS = {
  meta: 'bg-blue-600/15 text-blue-400 border-blue-500/25',
  google: 'bg-emerald-600/15 text-emerald-400 border-emerald-500/25',
  tiktok: 'bg-pink-600/15 text-pink-400 border-pink-500/25',
  linkedin: 'bg-sky-600/15 text-sky-400 border-sky-500/25',
  missionassist: 'bg-violet-600/15 text-violet-400 border-violet-500/25',
};


// ── Utility Components ──────────────────────────────────────

function StatusBadge({ status }) {
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${STATUS_STYLES[status] || STATUS_STYLES.inactive}`}
    >
      {status}
    </span>
  );
}

function PlatformBadge({ platform }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${PLATFORM_COLORS[platform] || 'bg-zinc-700/30 text-zinc-400 border-zinc-600/30'}`}>
      {PLATFORM_LABELS[platform] || platform}
    </span>
  );
}

function SyncIndicator({ syncStatus, syncError }) {
  const Icon = SYNC_ICONS[syncStatus] || Clock;
  const color = SYNC_COLORS[syncStatus] || 'text-zinc-500';
  return (
    <div className="flex items-center gap-1" title={syncError || syncStatus}>
      <Icon className={`w-3 h-3 ${color}`} />
      {syncError && <AlertCircle className="w-3 h-3 text-red-400" />}
    </div>
  );
}

function EntityCount({ count, label }) {
  if (!count) return null;
  return (
    <span className="text-[10px] text-zinc-500">{count} {label}</span>
  );
}

function ExpandToggle({ expanded, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-5 h-5 flex items-center justify-center rounded hover:bg-zinc-700/50 transition-colors"
    >
      {expanded
        ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
        : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
      }
    </button>
  );
}


// ── Hierarchy Row Components ────────────────────────────────

function CreativePreview({ creative }) {
  if (!creative) return <span className="text-[10px] text-zinc-600 italic">No creative</span>;
  return (
    <div className="flex items-center gap-2 ml-1">
      {creative.image_url && (
        <img src={creative.image_url} alt="" className="w-8 h-8 rounded object-cover border border-zinc-700" />
      )}
      <div className="min-w-0">
        {creative.headline && <div className="text-[10px] text-zinc-300 truncate max-w-[200px]">{creative.headline}</div>}
        {creative.primary_text && <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">{creative.primary_text}</div>}
      </div>
    </div>
  );
}

function AdRow({ ad, depth = 4 }) {
  return (
    <div
      data-testid={`ad-row-${ad.id}`}
      className="flex items-center gap-2 py-1.5 px-3 hover:bg-zinc-800/30 transition-colors group"
      style={{ paddingLeft: `${depth * 24 + 12}px` }}
    >
      <div className="w-5 h-5 flex items-center justify-center">
        <Radio className="w-3 h-3 text-zinc-600" />
      </div>
      <span className="text-xs text-zinc-300 truncate flex-1">{ad.name}</span>
      <CreativePreview creative={ad.creative} />
      <StatusBadge status={ad.status} />
      <SyncIndicator syncStatus={ad.sync_status} syncError={ad.sync_error} />
      {ad.external_id && (
        <span className="text-[9px] text-zinc-600 font-mono hidden group-hover:inline">{ad.external_id}</span>
      )}
    </div>
  );
}

function AdSetRow({ adSet, depth = 3, performanceData }) {
  const [expanded, setExpanded] = useState(false);
  const ads = adSet.ads || [];
  const metrics = performanceData?.[adSet.id];

  return (
    <div data-testid={`adset-row-${adSet.id}`}>
      <div
        className="flex items-center gap-2 py-1.5 px-3 hover:bg-zinc-800/40 transition-colors cursor-pointer"
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
        onClick={() => setExpanded(!expanded)}
      >
        {ads.length > 0
          ? <ExpandToggle expanded={expanded} onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} />
          : <div className="w-5" />
        }
        <Layers className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-xs font-medium text-zinc-200 truncate flex-1">{adSet.name}</span>
        <MetricsPills metrics={metrics} />
        <EntityCount count={adSet.ad_count} label="ads" />
        {adSet.budget && (
          <span className="text-[10px] text-zinc-500">
            ${adSet.budget?.toLocaleString()}/{adSet.budget_type === 'daily' ? 'day' : 'lifetime'}
          </span>
        )}
        {adSet.optimization_goal && (
          <span className="text-[10px] text-zinc-600">{adSet.optimization_goal}</span>
        )}
        <StatusBadge status={adSet.status} />
        <SyncIndicator syncStatus={adSet.sync_status} syncError={adSet.sync_error} />
      </div>
      {expanded && ads.map(ad => (
        <AdRow key={ad.id} ad={ad} depth={depth + 1} />
      ))}
    </div>
  );
}

function CampaignRow({ campaign, depth = 2, performanceData }) {
  const [expanded, setExpanded] = useState(false);
  const adSets = campaign.ad_sets || [];
  const metrics = performanceData?.[campaign.id];

  return (
    <div data-testid={`campaign-row-${campaign.id}`}>
      <div
        className="flex items-center gap-2 py-2 px-3 hover:bg-zinc-800/50 transition-colors cursor-pointer"
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
        onClick={() => setExpanded(!expanded)}
      >
        {adSets.length > 0
          ? <ExpandToggle expanded={expanded} onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} />
          : <div className="w-5" />
        }
        <Megaphone className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-sm font-medium text-zinc-100 truncate flex-1">{campaign.name}</span>
        <MetricsPills metrics={metrics} />
        <EntityCount count={campaign.ad_set_count} label="ad sets" />
        <EntityCount count={campaign.total_ad_count} label="ads" />
        {campaign.source && (
          <span className="text-[10px] text-zinc-600">{campaign.source}</span>
        )}
        <StatusBadge status={campaign.status || 'active'} />
        <SyncIndicator syncStatus={campaign.sync_status} syncError={campaign.sync_error} />
      </div>
      {expanded && adSets.map(adSet => (
        <AdSetRow key={adSet.id} adSet={adSet} depth={depth + 1} performanceData={performanceData} />
      ))}
    </div>
  );
}

function AccountRow({ account, performanceData }) {
  const [expanded, setExpanded] = useState(true);
  const campaigns = account.campaigns || [];

  return (
    <div data-testid={`account-row-${account.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden mb-3">
      <div
        className="flex items-center gap-2.5 py-3 px-4 cursor-pointer hover:bg-zinc-800/40 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <ExpandToggle expanded={expanded} onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} />
        <Building2 className="w-4 h-4 text-zinc-400" />
        <span className="text-sm font-semibold text-zinc-100 truncate flex-1">{account.name}</span>
        <PlatformBadge platform={account.platform} />
        <EntityCount count={account.campaign_count} label="campaigns" />
        <StatusBadge status={account.status} />
        <SyncIndicator syncStatus={account.sync_status} syncError={account.sync_error} />
        {account.external_id && (
          <span className="text-[10px] text-zinc-600 font-mono">{account.external_id}</span>
        )}
      </div>
      {expanded && (
        <div className="border-t border-zinc-800/60">
          {campaigns.length > 0 ? (
            campaigns.map(cmp => (
              <CampaignRow key={cmp.id} campaign={cmp} depth={1} performanceData={performanceData} />
            ))
          ) : (
            <div className="py-4 px-6 text-xs text-zinc-600">No campaigns linked to this account</div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Summary Stats ───────────────────────────────────────────

function SummaryBar({ summary }) {
  if (!summary) return null;
  const stats = [
    { label: 'Accounts', count: summary.total_accounts, icon: Building2 },
    { label: 'Campaigns', count: summary.total_campaigns, icon: Megaphone },
    { label: 'Ad Sets', count: summary.total_ad_sets, icon: Layers },
    { label: 'Ads', count: summary.total_ads, icon: Radio },
    { label: 'Creatives', count: summary.total_creatives, icon: FileImage },
  ];

  return (
    <div data-testid="advertising-summary" className="grid grid-cols-5 gap-3 mb-5">
      {stats.map(({ label, count, icon: Icon }) => (
        <div key={label} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center">
            <Icon className="w-4 h-4 text-zinc-400" />
          </div>
          <div>
            <div className="text-lg font-bold text-zinc-100">{count || 0}</div>
            <div className="text-[10px] text-zinc-500">{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Sync Status Panel ───────────────────────────────────────

function SyncStatusPanel({ syncStatus }) {
  if (!syncStatus?.entities) return null;
  const entities = syncStatus.entities;

  return (
    <div data-testid="sync-status-panel" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 mb-5">
      <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Sync Health</h3>
      <div className="grid grid-cols-5 gap-3">
        {Object.entries(entities).map(([type, data]) => {
          const total = data.total || 0;
          const synced = data.synced || 0;
          const failed = data.failed || 0;
          const pct = total > 0 ? Math.round((synced / total) * 100) : 0;

          return (
            <div key={type} className="space-y-1">
              <div className="text-[10px] text-zinc-500 capitalize">{type.replace('_', ' ')}</div>
              <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${failed > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-[9px] text-zinc-500">{synced}/{total}</span>
              </div>
              {failed > 0 && (
                <div className="text-[9px] text-red-400">{failed} failed</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Performance Metrics Badge ───────────────────────────────

function MetricsPills({ metrics }) {
  if (!metrics) return null;
  const { leads, appointments, clients, revenue } = metrics;
  const hasData = leads > 0 || appointments > 0 || clients > 0 || revenue > 0;
  if (!hasData) return null;

  return (
    <div className="flex items-center gap-1.5">
      {leads > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[9px] font-medium border border-blue-500/15">
          <Users className="w-2.5 h-2.5" />{leads}
        </span>
      )}
      {appointments > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[9px] font-medium border border-amber-500/15">
          <CalendarCheck className="w-2.5 h-2.5" />{appointments}
        </span>
      )}
      {clients > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[9px] font-medium border border-emerald-500/15">
          <CheckCircle2 className="w-2.5 h-2.5" />{clients}
        </span>
      )}
      {revenue > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-500/10 text-green-300 text-[9px] font-medium border border-green-500/15">
          <DollarSign className="w-2.5 h-2.5" />{revenue.toLocaleString()}
        </span>
      )}
    </div>
  );
}


// ── Empty State ─────────────────────────────────────────────

function EmptyState() {
  return (
    <div data-testid="advertising-empty" className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mb-4">
        <Building2 className="w-8 h-8 text-zinc-600" />
      </div>
      <h3 className="text-base font-semibold text-zinc-300 mb-1">No advertising accounts</h3>
      <p className="text-sm text-zinc-500 max-w-sm">
        Connect your ad platforms (Meta, Google Ads, TikTok) to see the full campaign hierarchy here.
      </p>
    </div>
  );
}


// ── Main Page ───────────────────────────────────────────────

export default function AdvertisingPage() {
  const [hierarchy, setHierarchy] = useState([]);
  const [unlinkedCampaigns, setUnlinkedCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [performanceData, setPerformanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [platformFilter, setPlatformFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const token = getToken();
  const headers = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchHierarchy = useCallback(async () => {
    try {
      const params = platformFilter ? `?platform=${platformFilter}` : '';
      const res = await fetch(`${API}/api/advertising/hierarchy${params}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setHierarchy(data.hierarchy || []);
      setUnlinkedCampaigns(data.unlinked_campaigns || []);
      setSummary(data.summary || null);
    } catch (e) {
      console.error('Failed to load hierarchy:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [headers, platformFilter]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const params = platformFilter ? `?platform=${platformFilter}` : '';
      const res = await fetch(`${API}/api/advertising/sync/status${params}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setSyncStatus(data);
    } catch { /* silent */ }
  }, [headers, platformFilter]);

  const fetchPerformance = useCallback(async () => {
    try {
      // Fetch performance for all group levels in parallel
      const [cmpRes, adsRes, adRes] = await Promise.all([
        fetch(`${API}/api/attribution/performance?group_by=campaign&days=90`, { headers }),
        fetch(`${API}/api/attribution/performance?group_by=ad_set&days=90`, { headers }),
        fetch(`${API}/api/attribution/performance?group_by=ad&days=90`, { headers }),
      ]);
      const perfMap = {};
      for (const res of [cmpRes, adsRes, adRes]) {
        if (res.ok) {
          const data = await res.json();
          for (const item of (data.items || [])) {
            perfMap[item.entity_id] = item.metrics;
          }
        }
      }
      setPerformanceData(perfMap);
    } catch { /* silent */ }
  }, [headers]);

  useEffect(() => {
    fetchHierarchy();
    fetchSyncStatus();
    fetchPerformance();
  }, [fetchHierarchy, fetchSyncStatus, fetchPerformance]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHierarchy();
    fetchSyncStatus();
    fetchPerformance();
  };

  // Filter hierarchy by search
  const filteredHierarchy = useMemo(() => {
    if (!searchQuery) return hierarchy;
    const q = searchQuery.toLowerCase();
    return hierarchy.filter(acc => {
      if (acc.name?.toLowerCase().includes(q)) return true;
      return (acc.campaigns || []).some(cmp =>
        cmp.name?.toLowerCase().includes(q) ||
        (cmp.ad_sets || []).some(ads =>
          ads.name?.toLowerCase().includes(q) ||
          (ads.ads || []).some(ad => ad.name?.toLowerCase().includes(q))
        )
      );
    });
  }, [hierarchy, searchQuery]);

  const allPlatforms = useMemo(() => {
    return [...new Set(hierarchy.map(a => a.platform).filter(Boolean))];
  }, [hierarchy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-zinc-700 border-t-zinc-400 rounded-full animate-spin" />
      </div>
    );
  }

  const isEmpty = hierarchy.length === 0 && unlinkedCampaigns.length === 0;

  return (
    <div data-testid="advertising-page" className="max-w-7xl mx-auto py-6 px-4 sm:px-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-zinc-100">Advertising</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Canonical hierarchy — Account, Campaign, Ad Set, Ad, Creative
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Platform filter */}
          {allPlatforms.length > 1 && (
            <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-0.5">
              <button
                onClick={() => setPlatformFilter(null)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${!platformFilter ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                All
              </button>
              {allPlatforms.map(p => (
                <button
                  key={p}
                  onClick={() => setPlatformFilter(p === platformFilter ? null : p)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${p === platformFilter ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  {PLATFORM_LABELS[p] || p}
                </button>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              data-testid="advertising-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-48 bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-zinc-200 focus:border-zinc-500 focus:outline-none placeholder:text-zinc-600"
            />
          </div>

          {/* Refresh */}
          <button
            data-testid="advertising-refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-400 bg-zinc-800 hover:bg-zinc-700 transition-colors border border-zinc-700"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <SummaryBar summary={summary} />

      {/* Sync Health */}
      {syncStatus && <SyncStatusPanel syncStatus={syncStatus} />}

      {/* Hierarchy Tree */}
      {isEmpty ? (
        <EmptyState />
      ) : (
        <div data-testid="advertising-hierarchy-tree">
          {filteredHierarchy.map(account => (
            <AccountRow key={account.id} account={account} performanceData={performanceData} />
          ))}

          {/* Unlinked campaigns (no ad account) */}
          {unlinkedCampaigns.length > 0 && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden mt-4">
              <div className="flex items-center gap-2.5 py-3 px-4 border-b border-zinc-800/60">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-zinc-300">
                  Unlinked Campaigns ({unlinkedCampaigns.length})
                </span>
                <span className="text-[10px] text-zinc-500">Not attached to an ad account</span>
              </div>
              {unlinkedCampaigns.map(cmp => (
                <CampaignRow key={cmp.id} campaign={cmp} depth={1} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
